/**
 * Салбарын гүйцэтгэл — энэ сарын цэвэр борлуулалт салбар тус бүрээр.
 *
 * ⚠️ «ЗОРИЛТООС ХУВЬ» ГАРГАХ БОЛОМЖГҮЙ. Прототипийн энэ блок нь
 *    төлөвлөгөөт тоотой харьцуулдаг байсан ч схемд төлөвлөгөө,
 *    зорилтын хүснэгт ОГТ БАЙХГҮЙ (34 хүснэгтийн аль нь ч биш).
 *    Иймд тууз нь зорилт биш, БОРЛУУЛАЛТЫН ЭЗЛЭХ ХУВЬ-ийг харуулна.
 *    Хуурамч зорилт зохиовол захирал түүнд тулгуурлаж шийдвэр гаргана.
 *
 * ⚠️ ЗӨВХӨН ЖИЖИГЛЭНГИЙН САЛБАР. Агуулах нь борлуулалтын цэг биш тул
 *    «гүйцэтгэл»-д оруулбал эзлэх хувь утгагүй болно. Агуулахын нөөц
 *    нь KPI мөрөнд болон Тайлан хуудсанд байна.
 *
 * ⚠️ ЦЭВЭР дүн: `/reports/sales` нь буцаагдсан мөр, VOID захиалгыг
 *    хасдаг. 8-р сард 34 мөр бүртгэгдсэнээс 32 нь буцаагдаж, цэвэр 2
 *    үлдсэн — түүхий тоо харуулбал 17 дахин хэтрүүлнэ.
 *
 * ⚠️ ӨНГӨ: бүх тууз НЭГ өнгөтэй. Салбар бүрийг өөр өнгөөр будвал
 *    өнгө нь ялгах утгагүй мэдээлэл зөөж, урт нь аль хэдийн хэлж
 *    байгааг давхардуулна (dataviz: нэг цуваа → нэг өнгө).
 */

import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { changePercent, formatMntShort, formatQty, revenueWindow } from '@boots/domain';
import { api, type Envelope } from '../../lib/api';
import type { BranchReport, SalesReport } from '../../lib/types';
import { Meter } from '../meter';
import { Section } from '../section';
import { EmptyState } from '../empty-state';
import { Skeleton } from '../skeleton';

/** Салбарын мөрөнд хэрэгтэй бүх тоо — нэг дор цуглуулсан. */
interface BranchRow {
  id: string;
  name: string;
  items: number;
  sellable: number;
  staff: number;
  units: number;
  revenueMnt: string;
  /** Өмнөх сартай харьцуулсан хувь. Өмнөх нь 0 бол null. */
  change: number | null;
}

export function BranchCard({ showValue }: { showValue: boolean }) {
  // Сарын хил УБ-ийн цагаар — `revenueWindow` нь Орлогын графиктай
  // ИЖИЛ логик. Энд дахин бичвэл хоёр блок өөр сар харуулж болно.
  const w = revenueWindow('month', new Date());

  const branches = useQuery({
    // Дашбоард аль хэдийн энэ түлхүүрээр татсан — дахин хүсэлт явахгүй
    queryKey: ['branches'],
    queryFn: () => api<Envelope<BranchReport>>('/reports/branches'),
    staleTime: 60_000,
  });

  const month = useQuery({
    queryKey: ['sales-by-store', 'month'],
    queryFn: () =>
      api<Envelope<SalesReport>>(
        `/reports/sales?groupBy=store&from=${encodeURIComponent(w.from.toISOString())}`,
      ),
    staleTime: 60_000,
  });

  const prev = useQuery({
    queryKey: ['sales-by-store', 'prev-month'],
    queryFn: () =>
      api<Envelope<SalesReport>>(
        `/reports/sales?groupBy=store` +
          `&from=${encodeURIComponent(w.prevFrom.toISOString())}` +
          `&to=${encodeURIComponent(w.prevTo.toISOString())}`,
      ),
    staleTime: 60_000,
  });

  const pending = branches.isPending || month.isPending || prev.isPending;

  const rows = buildRows(branches.data?.data, month.data?.data, prev.data?.data);
  const topRevenue = Math.max(...rows.map((r) => Number(r.revenueMnt)), 0);

  return (
    <Section
      title="Салбарын гүйцэтгэл"
      subtitle="энэ сард · цэвэр борлуулалт"
      actions={
        <Link to="/branches" className="text-meta text-accent-700 hover:underline">
          Дэлгэрэнгүй →
        </Link>
      }
    >
      {pending ? (
        <div className="flex flex-col gap-4">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      ) : rows.length === 0 ? (
        <EmptyState compact title="Жижиглэнгийн салбар бүртгэгдээгүй байна" />
      ) : (
        <ul className="flex flex-col gap-3.5">
          {rows.map((r) => (
            <li key={r.id} className="flex flex-col gap-1">
              <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5">
                <span className="min-w-0 text-body text-pretty">{r.name}</span>
                <span className="flex shrink-0 items-baseline gap-2.5">
                  {showValue ? (
                    <span className="num text-body whitespace-nowrap">
                      {r.units === 0 ? '—' : formatMntShort(r.revenueMnt)}
                    </span>
                  ) : null}
                  <span className="num text-meta whitespace-nowrap text-ink-2">
                    {formatQty(r.units)} ш
                  </span>
                  <Change value={r.change} />
                </span>
              </div>

              {/* Тууз = энэ сарын хамгийн өндөр борлуулалттай салбартай
                  харьцуулсан хувь. Зорилт БИШ — схемд зорилт байхгүй. */}
              <Meter
                value={topRevenue ? Math.round((Number(r.revenueMnt) / topRevenue) * 100) : 0}
                label={`${r.name} — ${formatQty(r.units)} ширхэг`}
              />

              <span className="text-label text-ink-3">{context(r)}</span>
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
}

/**
 * Салбарын тайлан ба хоёр сарын борлуулалтыг нэг мөр болгоно.
 *
 * Борлуулалтгүй салбар ч мөрөнд ҮЛДЭНЭ — «Салбар 2 юу ч зараагүй»
 * гэдэг нь өөрөө мэдээлэл. Шүүж хаявал асуудал нуугдана.
 */
function buildRows(
  report: BranchReport | undefined,
  month: SalesReport | undefined,
  prev: SalesReport | undefined,
): BranchRow[] {
  if (!report) return [];

  const now = new Map(month?.groups.map((g) => [g.key, g]) ?? []);
  const before = new Map(prev?.groups.map((g) => [g.key, g]) ?? []);

  return report.branches
    .filter((b) => b.type === 'RETAIL')
    .map((b) => {
      const cur = now.get(b.id);
      const old = before.get(b.id);
      return {
        id: b.id,
        name: b.name,
        items: b.items,
        sellable: b.sellable,
        staff: b.staff,
        units: cur?.units ?? 0,
        revenueMnt: cur?.revenueMnt ?? '0',
        change: old ? changePercent(cur?.revenueMnt ?? '0', old.revenueMnt) : null,
      };
    })
    .sort((a, b) => Number(b.revenueMnt) - Number(a.revenueMnt) || a.name.localeCompare(b.name));
}

/** Тэг мөр өөрийгөө тайлбарлана — яагаад зараагүйг нөөц, ажилтан хэлнэ. */
function context(r: BranchRow): string {
  const parts: string[] = [];
  parts.push(r.items === 0 ? 'нөөцгүй' : `нөөц ${formatQty(r.items)} ш`);
  if (r.items > 0) parts.push(`зарахад бэлэн ${formatQty(r.sellable)}`);
  parts.push(r.staff === 0 ? 'ажилтангүй' : `ажилтан ${formatQty(r.staff)}`);
  return parts.join(' · ');
}

/**
 * Өмнөх сартай харьцуулсан өөрчлөлт.
 *
 * ⚠️ Өнгө ГАНЦААРАА утга зөөхгүй — сум ба тэмдэг давхар кодолно.
 *    Борлуулалт ӨСӨХ нь САЙН (ханшийн картаас эсрэг утгатай).
 */
function Change({ value }: { value: number | null }) {
  if (value === null) return null;
  const up = value >= 0;
  return (
    <span className={`text-meta whitespace-nowrap ${up ? 'text-accent-700' : 'text-alert-ink'}`}>
      <span aria-hidden className="mr-0.5">{up ? '▲' : '▼'}</span>
      {up ? '+' : '−'}
      {Math.abs(value).toFixed(1)}%
    </span>
  );
}
